import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';

const FIELDS = ['name', 'email', 'phone', 'preferred_contact', 'contact_note'];

const buildInitial = (customer) => ({
  name: customer?.name || '',
  email: customer?.email || '',
  phone: customer?.phone || '',
  preferred_contact: customer?.preferred_contact || '',
  contact_note: customer?.contact_note || '',
});

const diff = (a, b) =>
  FIELDS.reduce((acc, k) => {
    if ((a[k] || '') !== (b[k] || '')) acc[k] = b[k];
    return acc;
  }, {});

export default function EditCustomerModal({ open, onClose, customer, onSave }) {
  const initial = useMemo(() => buildInitial(customer), [customer]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial, open]);

  const changes = diff(initial, form);
  const isDirty = Object.keys(changes).length > 0;

  const submit = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      await onSave(changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent data-testid="edit-customer-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit customer</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Saved as a Concierge working copy. The customer&apos;s account record is not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="ec-name" className="text-xs text-gray-600">Name</Label>
            <Input
              id="ec-name"
              data-testid="edit-customer-name-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ec-email" className="text-xs text-gray-600">Email</Label>
            <Input
              id="ec-email"
              type="email"
              data-testid="edit-customer-email-input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ec-phone" className="text-xs text-gray-600">Phone</Label>
            <Input
              id="ec-phone"
              data-testid="edit-customer-phone-input"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ec-preferred" className="text-xs text-gray-600">Preferred contact</Label>
            <Input
              id="ec-preferred"
              placeholder="e.g. email, phone, text after 5pm PT"
              data-testid="edit-customer-preferred-contact-input"
              value={form.preferred_contact}
              onChange={(e) => setForm((f) => ({ ...f, preferred_contact: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="ec-note" className="text-xs text-gray-600">Contact note</Label>
            <Textarea
              id="ec-note"
              rows={3}
              maxLength={500}
              placeholder="Anything the Concierge team should know about reaching this customer."
              data-testid="edit-customer-contact-note-input"
              value={form.contact_note}
              onChange={(e) => setForm((f) => ({ ...f, contact_note: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-customer-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!isDirty || saving}
            data-testid="edit-customer-save-btn"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
